import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import {
  getDeclarationTypeListAPI,
  getMyDeclarationAPI,
  addMyDeclarationAPI,
  editMyDeclarationAPI,
  getDeclarationApprovalAPI,
  getChangeApprovalStatusAPI,
  getYearListAPI,
  getUserDetailsAPI,
  addCOIChatAPI,
  getDeclarationApprovalDownloadAPI,
} from './DeclarationAPI';
import swal from 'sweetalert';

export interface DeclarationState {
  declarationTypelist: Array<any>;
  getMyDeclaration: any;
  addMyDeclaration: any;
  editMyDeclaration: any;
  changeStatus: any;
  declarationApprovalList: any;
  yearList: any;
  chatList: any;
  userDetails: any;
  status: 'idle' | 'loading' | 'failed';
  isLoading: boolean;
}

const initialState: DeclarationState = {
  declarationTypelist: [],
  getMyDeclaration: undefined,
  addMyDeclaration: undefined,
  editMyDeclaration: undefined,
  changeStatus: undefined,
  declarationApprovalList: undefined,
  yearList: undefined,
  chatList: undefined,
  userDetails: undefined,
  status: 'idle',
  isLoading: false,
};

type DownloadPayload = {
  blob: Blob;
  fileName: string;
  contentType?: string;
};

export const GetMyDeclarationAsync = createAsyncThunk(
  'declaration/getMyDeclaration',
  async (data: any) => {
    const response = await getMyDeclarationAPI(data);
    return response;
  },
);

export const AddMyDeclarationAsync = createAsyncThunk(
  'declaration/addMyDeclaration',
  async (data: any) => {
    const response = await addMyDeclarationAPI(data);
    return response;
  },
);

export const EditMyDeclarationAsync = createAsyncThunk(
  'declaration/editMyDeclaration',
  async (data: any) => {
    const response = await editMyDeclarationAPI(data);
    return response;
  },
);

export const GetDeclarationTypeListAsync = createAsyncThunk(
  'declaration/fetchDeclarationType',
  async () => {
    const response = await getDeclarationTypeListAPI();
    return response;
  },
);

export const UpdateDeclarationStatusAsync = createAsyncThunk(
  'declaration/updateDeclarationStatus',
  async (data: any) => {
    const response = await getChangeApprovalStatusAPI(data);
    return response;
  },
);

export const GetApprovalDeclarationListAsync = createAsyncThunk(
  'declaration/getApprovalDeclarationList',
  async (data: any) => {
    const response = await getDeclarationApprovalAPI(data);
    return response;
  },
);


export const getDeclarationApprovalDownloadAsync = createAsyncThunk<
  DownloadPayload,
  void
>(
  'declaration/getApprovalDeclarationDownload',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getDeclarationApprovalDownloadAPI();

      // Try to determine filename from header
      const disposition = response.headers?.['content-disposition'] || '';
      let fileName = 'Declaration.xlsx';

      // Common patterns: attachment; filename="abc.xlsx" or filename*=UTF-8''abc.xlsx
      const filenameMatch =
        disposition.match(/filename\*?=(?:UTF-8''|")(.*?)(?:\"|;|$)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);

      if (filenameMatch && filenameMatch[1]) {
        // Decode RFC5987 or quoted string if needed
        try {
          fileName = decodeURIComponent(filenameMatch[1]);
        } catch {
          fileName = filenameMatch[1];
        }
      }

      const contentType = response.headers?.['content-type'];

      return {
        blob: response.data,
        fileName,
        contentType,
      };
    } catch (err: any) {
      // When servers return error as blob, you might want to read it and parse message
      return rejectWithValue({ message: 'Failed to download report', raw: err });
    }
  }
);




export const GetYearListAsync = createAsyncThunk(
  'declaration/fetchYearList',
  async () => {
    const response = await getYearListAPI();
    return response;
  },
);

export const GetUserDetailsAsync = createAsyncThunk(
  'declaration/getUserDetails',
  async () => {
    const response = await getUserDetailsAPI();
    return response;
  },
);

export const AddCOIChatAsync = createAsyncThunk(
  'declaration/addCOIChat',
  async (data: any) => {
    const response = await addCOIChatAPI(data);
    return response;
  },
);

export const DeclarationSlice = createSlice({
  name: 'declaration',
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder
      .addCase(GetDeclarationTypeListAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(GetDeclarationTypeListAsync.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(GetDeclarationTypeListAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.declarationTypelist = action.payload;
      })

      .addCase(GetMyDeclarationAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(GetMyDeclarationAsync.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(GetMyDeclarationAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.getMyDeclaration = action.payload;
      })

      .addCase(AddMyDeclarationAsync.pending, (state) => {
        state.status = 'loading';
        state.isLoading = true;
      })
      .addCase(AddMyDeclarationAsync.rejected, (state) => {
        state.status = 'failed';
        swal('Something went wrong!', 'error');
        state.isLoading = false;
      })
      .addCase(AddMyDeclarationAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.addMyDeclaration = action.payload;
        state.isLoading = false;
        // swal('Declaration Added Successfully', 'success');
      })

      .addCase(EditMyDeclarationAsync.pending, (state) => {
        state.status = 'loading';
        state.isLoading = true;
      })
      .addCase(EditMyDeclarationAsync.rejected, (state) => {
        state.status = 'failed';
        swal('Something went wrong!', 'error');
        state.isLoading = false;
      })
      .addCase(EditMyDeclarationAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.editMyDeclaration = action.payload;
        state.isLoading = false;
        // swal('Declaration Updated Successfully', 'success');
      })

      .addCase(UpdateDeclarationStatusAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(UpdateDeclarationStatusAsync.rejected, (state) => {
        state.status = 'failed';
        swal('Something went wrong!', 'error');
      })
      .addCase(UpdateDeclarationStatusAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.changeStatus = action.payload;
        // swal('Updated Declaration Status', 'success');
      })

      .addCase(GetApprovalDeclarationListAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(GetApprovalDeclarationListAsync.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(GetApprovalDeclarationListAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.declarationApprovalList = action.payload;
      })

      .addCase(GetYearListAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(GetYearListAsync.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(GetYearListAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.yearList = action.payload;
      })

      .addCase(GetUserDetailsAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(GetUserDetailsAsync.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(GetUserDetailsAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.userDetails = action.payload;
      })

      .addCase(AddCOIChatAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(AddCOIChatAsync.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(AddCOIChatAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.chatList = action.payload;
      });
  },
});

export const {} = DeclarationSlice.actions;

export const selectDeclarationTypeList = (state: RootState) => {
  return state.declaration.declarationTypelist;
};

export const selectGetMyDeclaration = (state: RootState) => {
  return state.declaration.getMyDeclaration;
};

export const selectAddMyDeclaration = (state: RootState) => {
  return state.declaration.addMyDeclaration;
};

export const selectEditMyDeclaration = (state: RootState) => {
  return state.declaration.editMyDeclaration;
};

export const selectPendingDeclaration = (state: RootState) => {
  return state.declaration.declarationApprovalList;
};

export const selectUpdateDeclarationStatus = (state: RootState) => {
  return state.declaration.changeStatus;
};

export const selectYearList = (state: RootState) => {
  return state.declaration.yearList;
};

export const selectUserDetails = (state: RootState) => {
  return state.declaration.userDetails;
};

export const selectCOIChatList = (state: RootState) => {
  return state.declaration.chatList;
};

export const selectLoadingState = (state: RootState) => {
  return state.declaration.isLoading;
};

export default DeclarationSlice.reducer;
